/**
 * Conversational AvA Onboarding (Schools Programme — Phase B).
 *
 * VISUAL: identical chrome to ConversationsChat — DashboardLayout shell,
 * left panel with `IdleLoopVideo` of the user's primary AvA wrapped in
 * MoodAuraRing, name + Speaking/Thinking/Ready dot. Right panel is a
 * scrolling chat log + an input/scale/choice affordance docked at the
 * bottom.
 *
 * Falls back to a static avatar image if the AvA has no idle-loop video.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import MoodAuraRing from '../components/engagement/MoodAuraRing';
import { IdleLoopVideo } from '../components/chat/IdleLoopVideo';
import { apiClient } from '../api/client';
import CacheManager from '../utils/cacheManager';

const SCALE_LABELS = ['1', '2', '3', '4', '5'];
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Bubble = ({ from, children, testId, avaName }) => (
  <div
    className={`flex ${from === 'ava' ? 'justify-start' : 'justify-end'} mb-3`}
    data-testid={testId || `onboarding-bubble-${from}`}
  >
    <div
      className={`max-w-md rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm ${
        from === 'ava'
          ? 'bg-white text-gray-800 border border-gray-200'
          : 'bg-purple-600 text-white'
      }`}
    >
      {from === 'ava' && avaName && (
        <p className="text-xs font-semibold text-purple-700 mb-1">{avaName}</p>
      )}
      {children}
    </div>
  </div>
);

const OnboardingFlow = ({ onComplete }) => {
  const navigate = useNavigate();
  const [script, setScript] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [history, setHistory] = useState([]);
  const [answers, setAnswers] = useState({});
  const [prefs, setPrefs] = useState({});
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [primaryAva, setPrimaryAva] = useState(null);
  const [idleLoopUrl, setIdleLoopUrl] = useState(null);
  const scrollerRef = useRef(null);

  // Fetch the script
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.request('/api/schools/onboarding/script');
        if (!cancelled) {
          setScript(res.script);
          if (res.script[0]?.type === 'ava') {
            setHistory([{ from: 'ava', text: res.script[0].text }]);
            setStepIdx(1);
          }
        }
      } catch (_e) {
        setScript([{ id: 'intro', type: 'ava', text: 'Welcome.' }]);
        setError('Could not load AvA — please sign in and retry.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the user's primary AvA so we mirror their existing partner here
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiClient.request('/api/avas');
        if (cancelled) return;
        const primary = (list || []).find((a) => a.is_primary) || (list || [])[0] || null;
        setPrimaryAva(primary);
      } catch (_e) {
        if (!cancelled) setPrimaryAva(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the idle-loop video for the primary AvA (same path ConversationsChat uses)
  useEffect(() => {
    if (!primaryAva?.ava_id) {
      setIdleLoopUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await CacheManager.fetchWithCache(
          `idle_loop_${primaryAva.ava_id}`,
          () => apiClient.request(`/api/ava-video/idle-loop-for-ava/${primaryAva.ava_id}`),
          10 * 60 * 1000,
        );
        if (cancelled) return;
        if (data?.idle_loop_url) {
          setIdleLoopUrl(`${API_URL}/api/static/${data.idle_loop_url}`);
        } else {
          setIdleLoopUrl(null);
        }
      } catch (_e) {
        if (!cancelled) setIdleLoopUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [primaryAva?.ava_id]);

  // Auto-scroll
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, stepIdx]);

  const step = script ? script[stepIdx] : null;
  const totalSteps = script ? script.length : 0;
  const progressPct = totalSteps ? Math.round(((stepIdx + 1) / totalSteps) * 100) : 0;
  const answeredCount = Object.keys(answers).length + Object.keys(prefs).length;
  const avaName = primaryAva?.name || 'AvA';
  const avaImage = primaryAva?.avatar_url || primaryAva?.avatar_config?.image_url || null;

  // Replace the generic "AVA" in script copy with the user's actual partner name
  const personalize = (text) => {
    if (!text || !primaryAva?.name) return text;
    return text
      .replace(/I['']m AvA\b/gi, `I'm ${avaName}`)
      .replace(/I['']m AVA\b/g, `I'm ${avaName}`)
      .replace(/with AvA\b/g, `with ${avaName}`);
  };

  // Re-personalize history when AvA name resolves (covers the case where the
  // script's intro bubble was loaded before the primary AvA was fetched).
  // We do this by storing raw text + applying `personalize` at render time
  // (see the history.map below). No effect needed.

  const advanceAva = (fromIdx) => {
    if (!script) return;
    let i = fromIdx;
    const bubbles = [];
    while (i < script.length && script[i].type === 'ava') {
      bubbles.push({ from: 'ava', text: script[i].text });
      i += 1;
    }
    if (bubbles.length) setHistory((h) => [...h, ...bubbles]);
    setStepIdx(i);
  };

  const acceptText = (text) => {
    if (!step || !text || !text.trim()) return;
    setHistory((h) => [
      ...h,
      { from: 'ava', text: step.ava_text },
      { from: 'teen', text: text.trim() },
    ]);
    setPrefs((p) => ({ ...p, [step.field]: text.trim() }));
    setDraft('');
    advanceAva(stepIdx + 1);
  };

  const acceptScale = (value) => {
    if (!step) return;
    setHistory((h) => [
      ...h,
      { from: 'ava', text: step.ava_text },
      { from: 'teen', text: `${value} / 5` },
    ]);
    setAnswers((a) => ({ ...a, [step.id]: value }));
    advanceAva(stepIdx + 1);
  };

  const acceptChoice = (choice) => {
    if (!step) return;
    setHistory((h) => [
      ...h,
      { from: 'ava', text: step.ava_text },
      { from: 'teen', text: choice },
    ]);
    setPrefs((p) => ({ ...p, [step.field]: choice }));
    advanceAva(stepIdx + 1);
  };

  const isComplete = !!script && stepIdx >= script.length;

  const submit = async () => {
    if (Object.keys(answers).length !== 15) {
      setError('You must answer every question — failure to do so will prevent you from completing your registration.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await apiClient.request('/api/schools/onboarding/submit', {
        method: 'POST',
        body: JSON.stringify({ answers, preferences: prefs }),
      });
      try {
        await apiClient.request('/api/users/onboarding', {
          method: 'POST',
          body: JSON.stringify({ primary_modal: 'happiness', first_goal: prefs.one_word_goal || '' }),
        });
      } catch (_e) { /* non-fatal */ }
      if (onComplete) onComplete();
      // CR-11 EXT: route the teen straight into Practice on their first
      // assigned 20-challenge so the session is bound to a challenge_run.
      // If the journey lookup fails or returns no active challenge, fall
      // back to freestyle Practice (unchanged behaviour).
      try {
        const cur = await apiClient.request('/api/schools/journey/current');
        if (cur && cur.has_journey && cur.challenge_id) {
          navigate(`/conversations?challenge_id=${encodeURIComponent(cur.challenge_id)}`);
          return;
        }
      } catch (_e) { /* non-fatal — fall through to freestyle */ }
      navigate('/conversations');
    } catch (e) {
      setError(e?.message || 'Could not save your onboarding answers.');
      setSubmitting(false);
    }
  };

  // Active interactive affordance — input / scale / choice
  const Active = useMemo(() => {
    if (!script) return null;
    if (isComplete) {
      return (
        <div
          className="bg-white border border-gray-200 rounded-2xl p-6 text-center shadow-sm"
          data-testid="onboarding-finish-card"
        >
          <Sparkles className="mx-auto mb-3 text-amber-500" size={36} />
          <h2 className="text-gray-900 text-xl font-semibold mb-1">All done</h2>
          <p className="text-gray-600 text-sm mb-5">
            I&apos;ll use what you just told me to shape your first challenges. Tap continue and we&apos;ll start.
          </p>
          {error && (
            <p className="text-red-500 text-xs mb-3" data-testid="onboarding-error">{error}</p>
          )}
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="onboarding-submit-button"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
            Begin my first challenge
          </button>
        </div>
      );
    }
    if (!step) return null;
    if (step.type === 'input') {
      return (
        <div className="space-y-3" data-testid={`onboarding-input-${step.id}`}>
          <Bubble from="ava" avaName={avaName}>{personalize(step.ava_text)}</Bubble>
          <div className="flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && acceptText(draft)}
              placeholder={step.placeholder}
              className="flex-1 px-4 py-3 rounded-xl bg-white text-gray-900 placeholder-gray-400 border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
              autoFocus
              data-testid={`onboarding-input-field-${step.id}`}
            />
            <button
              onClick={() => acceptText(draft)}
              disabled={!draft.trim()}
              className="p-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-40 flex items-center justify-center"
              data-testid={`onboarding-input-submit-${step.id}`}
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      );
    }
    if (step.type === 'choice') {
      return (
        <div className="space-y-3" data-testid={`onboarding-choice-${step.id}`}>
          <Bubble from="ava" avaName={avaName}>{personalize(step.ava_text)}</Bubble>
          <div className="grid grid-cols-2 gap-2">
            {step.options.map((opt) => (
              <button
                key={opt}
                onClick={() => acceptChoice(opt)}
                className="px-4 py-3 rounded-xl bg-white text-gray-800 border border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-sm font-medium transition-colors"
                data-testid={`onboarding-choice-option-${step.id}-${opt}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (step.type === 'scale') {
      return (
        <div className="space-y-3" data-testid={`onboarding-scale-${step.id}`}>
          <Bubble from="ava" avaName={avaName}>{personalize(step.ava_text)}</Bubble>
          <div className="flex justify-between text-xs text-gray-500 px-1">
            <span>{step.low}</span>
            <span>{step.high}</span>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {SCALE_LABELS.map((lbl, i) => (
              <button
                key={lbl}
                onClick={() => acceptScale(i + 1)}
                className="py-3 rounded-xl bg-white text-gray-700 border border-gray-200 hover:border-purple-500 hover:bg-purple-50 text-sm font-semibold transition-colors"
                data-testid={`onboarding-scale-${step.id}-${i + 1}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return null;
    // Same rationale as `MayaProfileChat`: this useMemo is a pure JSX
    // renderer of the current step's input widget. It references event
    // handlers (`acceptText`, `acceptChoice`, `acceptScale`, `personalize`,
    // `submit`) — adding those to deps would rebuild the memo on every
    // keystroke of an unrelated field, defeating the memoization. Keyed
    // on the primary drivers that actually change what's rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script, step, draft, isComplete, submitting, error, answers, prefs, avaName]);

  if (!script) {
    return (
      <DashboardLayout>
        <div className="min-h-[60vh] flex items-center justify-center text-gray-400">
          <Loader2 className="animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Top progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
            <span className="font-semibold uppercase tracking-wider">
              Onboarding with {avaName}
            </span>
            <span data-testid="onboarding-progress-label">
              {Math.min(stepIdx + 1, totalSteps)} / {totalSteps} · {answeredCount} answered
            </span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-orange-500 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>

        <div
          className="flex flex-col lg:flex-row bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
          data-testid="onboarding-flow"
        >
          {/* ── Left panel — AvA display (same as ConversationsChat desktop) ── */}
          <div
            className="hidden lg:flex w-96 flex-shrink-0 p-6 bg-gradient-to-b from-slate-50 to-slate-100 border-r border-gray-200 flex-col items-center"
            data-testid="onboarding-ava-panel"
          >
            <MoodAuraRing className="rounded-2xl">
              <div
                className="relative w-72 h-72 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white"
                data-testid="onboarding-ava-portrait"
              >
                {idleLoopUrl ? (
                  <IdleLoopVideo
                    src={idleLoopUrl}
                    poster={avaImage || undefined}
                    className="w-full h-full object-cover"
                    testId="onboarding-ava-idle-loop-video"
                  />
                ) : avaImage ? (
                  <img
                    src={avaImage}
                    alt={avaName}
                    className="w-full h-full object-cover"
                    data-testid="onboarding-ava-static-image"
                    onError={(e) => {
                      e.currentTarget.src = `https://api.dicebear.com/7.x/lorelei/svg?seed=${encodeURIComponent(avaName)}&backgroundColor=c7d2fe`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-300 to-pink-200 flex items-center justify-center text-6xl font-bold text-white">
                    {avaName[0]}
                  </div>
                )}
              </div>
            </MoodAuraRing>

            <div className="mt-6 text-center">
              <h3 className="text-2xl font-bold text-gray-900" data-testid="onboarding-ava-name">
                {avaName}
              </h3>
              <p className="text-xs text-purple-600 font-semibold uppercase tracking-wider mt-0.5">
                Your practice partner
              </p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                <span className="text-base text-gray-600 font-medium">Ready</span>
              </div>
            </div>

            <div className="mt-6 w-full rounded-xl bg-white/80 border border-gray-200 p-4">
              <p className="text-xs font-semibold text-gray-700 mb-1">What we&apos;re doing</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                8 quick preference questions, then 15 short scenarios so {avaName} can shape your first challenges.
                There are no wrong answers — just honest ones.
              </p>
              <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="onboarding-mandatory-warning">
                ⚠️ Every question must be answered — failure to do so will prevent you from completing your registration.
              </p>
            </div>
          </div>

          {/* ── Mobile — compact AvA bar above the chat ───────────────────── */}
          <div className="lg:hidden flex items-center gap-3 px-3 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-gray-200">
            <div className="relative w-16 h-16 rounded-2xl overflow-hidden ring-2 ring-white shadow-md flex-shrink-0">
              {idleLoopUrl ? (
                <IdleLoopVideo
                  src={idleLoopUrl}
                  poster={avaImage || undefined}
                  className="w-full h-full object-cover"
                />
              ) : avaImage ? (
                <img src={avaImage} alt={avaName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-300 to-pink-200 flex items-center justify-center text-2xl font-bold text-white">
                  {avaName[0]}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-gray-900 truncate">{avaName}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-gray-600">Ready · onboarding</span>
              </div>
            </div>
          </div>

          {/* ── Right panel — chat log + active step ─────────────────────── */}
          <div className="flex-1 min-w-0 flex flex-col" data-testid="onboarding-chat-panel">
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto p-5 bg-gradient-to-b from-slate-50 to-white min-h-[340px] max-h-[460px]"
              data-testid="onboarding-chat-history"
            >
              {history.map((h, i) => (
                <Bubble key={i} from={h.from} avaName={h.from === 'ava' ? avaName : undefined}>
                  {h.from === 'ava' ? personalize(h.text) : h.text}
                </Bubble>
              ))}
            </div>

            <div className="p-5 bg-white border-t border-gray-200" data-testid="onboarding-active-step">
              {Active}
            </div>
          </div>
        </div>

        <p
          className="text-center text-xs text-gray-500 mt-4 italic px-4"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Brave takes courage. Courage builds confidence. Confidence builds character. And character is the foundation of friends forever.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default OnboardingFlow;
